import { useCallback, useEffect } from "react"
import { toast } from "react-hot-toast";
import { WalletInfo } from "types/baseType"
import { Chain, useAccount, useConnect } from "wagmi"
import { InjectedConnector } from "wagmi/connectors/injected";
import { WalletConnectConnector } from "wagmi/connectors/walletConnect";
import { useAppDispatch, useAppSelector } from "./redux/hooks";
import { getStore, setStore } from "utils/store";
import { setIsConnected } from "./redux/slices/walletSlice";

const useWalletConnect = () => {
    const { wagmiChains, brc20 } = useAppSelector((state) => state.app)
    const { fromChain } = useAppSelector((state) => state.coreInfo)
    const { connect, isSuccess } = useConnect()
    const { isConnected } = useAccount()
    const dispatch = useAppDispatch()

    const getWalletProvider = useCallback((walletName: string) => {
        const originName = walletName
        walletName = walletName.toLowerCase()
        let errorType = '' // conflict uninstalled
        if(typeof window === 'undefined') return undefined
        if(!(window as any).ethereum) {
            errorType = 'uninstalled'
            return undefined
        }
        const defaultEthereum = (window as any).ethereum
        let resultEthereum = undefined
        // token pocket
        const isTokenPocket = defaultEthereum.isTokenPocket
        // coinbase
        const isCoinbase = defaultEthereum.providers
        // okx
        const isOkx = (window as any).okxwallet !== undefined
        // pali
        const isPali = (window as any).pali !== undefined
        if(walletName === 'metamask') {
            resultEthereum = defaultEthereum
            if(isTokenPocket || isOkx) {
                errorType = 'conflict'
                resultEthereum = undefined
            }
            if(isPali) {
                if(defaultEthereum.wallet && defaultEthereum.wallet === 'pali-v2') {
                    errorType = 'conflict'
                    resultEthereum = undefined
                }
            }
            if(isCoinbase) {
                const index = defaultEthereum.providers.findIndex((item: any) => item.isMetaMask)
                if(index > -1) {
                    resultEthereum = defaultEthereum.providers[index]
                }
            }
        } else if(walletName === 'bitgetwallet') {
            if(typeof (window as any).bitkeep === 'undefined') {
                errorType = 'uninstalled'
                resultEthereum = undefined
            }
            resultEthereum = (window as any).bitkeep?.ethereum
        } else if(walletName === 'okxwallet') {
            if(typeof (window as any).okxwallet === 'undefined') {
                errorType = 'uninstalled'
                resultEthereum = undefined
            }
            resultEthereum = (window as any).okxwallet
        } else if(walletName === 'tokenpocket') {
            if(!isTokenPocket) {
                errorType = 'uninstalled'
                resultEthereum = undefined
            }
            resultEthereum = defaultEthereum
        } else if(walletName === 'coinbase') {
            resultEthereum = defaultEthereum

            if(isTokenPocket || isOkx || isPali) {
                errorType = 'conflict'
                resultEthereum = undefined
            }
            if(!isCoinbase) {
                errorType = 'uninstalled'
                resultEthereum = undefined
            }
            if(isCoinbase) {
                const index = defaultEthereum.providers.findIndex((item: any) => item.isCoinbaseWallet)
                if(index > -1) {
                    resultEthereum = defaultEthereum.providers[index]
                }
            }
        } else if(walletName === 'pali') {
            if(defaultEthereum.wallet && defaultEthereum.wallet === 'pali-v2') {
                resultEthereum = defaultEthereum
              } else {
                errorType = 'uninstalled'
                resultEthereum = undefined
              }
        }

        if(errorType) {
            let errorMsg = ''
            if(errorType === 'conflict') {
                errorMsg = `If you have installed ${originName}, go to browser settings to enable it and disbale other wallets.`
            } else {
                errorMsg = `${originName} not installed`
            }
            toast(errorMsg,
                {
                  duration: 3000,
                  position: 'top-right',
                  icon: '⚠️'
                }
            );
        } else {
            return resultEthereum
        }
    }, [])

    const fnWalletconnect = useCallback(() => {
        if(!(wagmiChains && fromChain)) return
        const tempChainList = [...wagmiChains]
        const index = tempChainList.findIndex((item: Chain) => item.id === +fromChain.network)
        if(index > -1) {
            const curChain = tempChainList.splice(index, 1)[0]
            tempChainList.unshift(curChain)
        }

        const connectorss = new WalletConnectConnector({
            chains: tempChainList,
            options: {
            projectId: '565227bd5a9f33e793661fa48e7570a2'
            },
        })
        connect({
            connector: connectorss
        })
    }, [connect, fromChain, wagmiChains])

    const connectWallet = useCallback((walletInfo: WalletInfo) => {
        try {
            if(isConnected) return;
            const walletName = walletInfo.name.toLowerCase()
            if(walletName === 'walletconnect') {
                fnWalletconnect()
            } else {
                const provider = getWalletProvider(walletInfo.name)
                if(!provider) return
                setStore('chainge.curWallet', walletName)
                connect({
                    connector: new InjectedConnector({
                        chains: wagmiChains,
                        options: {
                            name: walletInfo.name,
                            shimDisconnect: true,
                            getProvider: () =>  provider
                        }
                    })
                })
            }
        } catch(error) {

        }
    }, [connect, fnWalletconnect, getWalletProvider, isConnected, wagmiChains])

    // 刷新界面重新链接
    useEffect(() => {
        if(brc20) return
        const curWalletName = getStore('chainge.curWallet')
        if(!isConnected && curWalletName && !['unisat', 'okx_bitcoin'].includes(curWalletName) && wagmiChains.length) {
            try {
                const provider = getWalletProvider(curWalletName)
                if(!provider) return
                connect({
                    connector: new InjectedConnector({
                        chains: wagmiChains,
                        options: {
                            name: curWalletName,
                            shimDisconnect: true,
                            getProvider: () =>  provider
                        }
                    })
                })
            } catch(error) {
                
            }
        }
    }, [brc20, connect, getWalletProvider, isConnected, wagmiChains])

    useEffect(() => {
        dispatch(setIsConnected(isConnected))
    }, [dispatch, isConnected])

    return {connectWallet, isSuccess}
}

export default useWalletConnect